import React from 'react';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/ui/badge';
import { Check, Clock, DoorOpen, X } from 'lucide-react';
import { motion } from 'framer-motion';

export default function WeekSummary() {
  const { campDates, campers, getCampersForDate, getAttendance } = useApp();

  // Get all unique campers across all days
  const allCamperIds = new Set();
  campDates.forEach(d => {
    getCampersForDate(d.date).forEach(c => allCamperIds.add(c.id));
  });

  const camperData = [...allCamperIds].map(id => {
    const camper = campers.find(c => c.id === id);
    if (!camper) return null;

    const dayStatus = {};
    campDates.forEach(d => {
      const attending = camper.daysAttending && camper.daysAttending.includes(d.date);
      if (attending) {
        const att = getAttendance(id, d.date);
        dayStatus[d.date] = { attending: true, status: att.status, late: !!att.lateTime, leaving: !!att.earlyTime };
      } else {
        dayStatus[d.date] = { attending: false };
      }
    });

    return { id, firstName: camper.firstName, lastName: camper.lastName, group: camper.group, dayStatus };
  }).filter(Boolean).sort((a, b) => {
    if (a.group !== b.group) {
      if (a.group === 'Unassigned') return 1;
      if (b.group === 'Unassigned') return -1;
      return a.group.localeCompare(b.group);
    }
    return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
  });

  const groupedCampers = {};
  camperData.forEach(c => {
    if (!groupedCampers[c.group]) groupedCampers[c.group] = [];
    groupedCampers[c.group].push(c);
  });

  const getStatusIcon = (status) => {
    if (!status.attending) return <span className="text-gray-300">-</span>;
    if (status.status === 'absent') return <X className="w-4 h-4 text-gray-400" />;
    if (status.late && status.leaving) return <div className="flex items-center gap-0.5"><Clock className="w-3 h-3 text-amber-500" /><DoorOpen className="w-3 h-3 text-rose-500" /></div>;
    if (status.late) return <Clock className="w-4 h-4 text-amber-500" />;
    if (status.leaving) return <DoorOpen className="w-4 h-4 text-rose-500" />;
    return <Check className="w-4 h-4 text-green-500" />;
  };

  return (
    <div className="space-y-4">
      <motion.div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="p-4 border-b border-[#E2E8F0]">
          <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka']">Week Summary</h3>
          <p className="text-sm text-[#475569] mt-1">Holiday Camp Spring 2026 - All Campers</p>
        </div>

        <div className="px-4 py-3 bg-gray-50 border-b border-[#E2E8F0] flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-1"><Check className="w-4 h-4 text-green-500" /><span>Present</span></div>
          <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-amber-500" /><span>Late</span></div>
          <div className="flex items-center gap-1"><DoorOpen className="w-4 h-4 text-rose-500" /><span>Leaving Early</span></div>
          <div className="flex items-center gap-1"><X className="w-4 h-4 text-gray-400" /><span>Absent</span></div>
          <div className="flex items-center gap-1"><span className="text-gray-300">-</span><span>Not Enrolled</span></div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#0055A4] text-white">
                <th className="sticky left-0 bg-[#0055A4] text-left p-3 font-semibold text-sm min-w-[180px]">Camper</th>
                {campDates.map(d => (
                  <th key={d.date} className="p-3 font-semibold text-sm text-center min-w-[60px]">{d.label.split(' ')[0]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedCampers).map(([groupName, groupCampers]) => (
                <React.Fragment key={groupName}>
                  <tr className="bg-blue-50">
                    <td colSpan={campDates.length + 1} className="sticky left-0 bg-blue-50 p-2 font-bold text-[#0055A4] text-sm">{groupName}</td>
                  </tr>
                  {groupCampers.map((camper, index) => (
                    <tr key={camper.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`week-row-${camper.id}`}>
                      <td className={`sticky left-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} p-3 text-sm font-medium text-[#0F172A] border-r border-[#E2E8F0]`}>
                        {camper.firstName} {camper.lastName}
                      </td>
                      {campDates.map(d => (
                        <td key={d.date} className="p-3 text-center">
                          <div className="flex items-center justify-center">{getStatusIcon(camper.dayStatus[d.date])}</div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {camperData.length === 0 && (
          <div className="p-8 text-center text-[#475569]">No camper data available. Click Refresh to load from Google Sheet.</div>
        )}
      </motion.div>

      {/* Daily Totals */}
      <motion.div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <h3 className="text-sm font-bold text-[#475569] uppercase tracking-wider mb-4">Daily Totals</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E2E8F0]">
                <th className="text-left p-2 font-semibold text-sm text-[#475569]">Day</th>
                <th className="p-2 font-semibold text-sm text-[#475569] text-center">Present</th>
                <th className="p-2 font-semibold text-sm text-[#475569] text-center">Late</th>
                <th className="p-2 font-semibold text-sm text-[#475569] text-center">Leaving</th>
                <th className="p-2 font-semibold text-sm text-[#475569] text-center">Absent</th>
                <th className="p-2 font-semibold text-sm text-[#475569] text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {campDates.map((d, index) => {
                const dayCampers = getCampersForDate(d.date);
                const stats = dayCampers.reduce((acc, camper) => {
                  const att = getAttendance(camper.id, d.date);
                  if (att.status === 'absent') { acc.absent++; }
                  else { acc.present++; if (att.lateTime) acc.late++; if (att.earlyTime) acc.leaving++; }
                  return acc;
                }, { present: 0, late: 0, leaving: 0, absent: 0 });

                return (
                  <tr key={d.date} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="p-2 font-medium text-[#0F172A]">{d.label}</td>
                    <td className="p-2 text-center"><Badge className="status-present">{stats.present}</Badge></td>
                    <td className="p-2 text-center"><Badge className="status-late">{stats.late}</Badge></td>
                    <td className="p-2 text-center"><Badge className="status-leaving">{stats.leaving}</Badge></td>
                    <td className="p-2 text-center"><Badge className="status-absent">{stats.absent}</Badge></td>
                    <td className="p-2 text-center"><Badge className="bg-[#0055A4] text-white">{dayCampers.length}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
